import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Leaf, Menu, X, LogOut, LayoutDashboard } from 'lucide-react';
import { COLORS, NAV_LINKS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { isPrelaunch } from '../data/launch';
import { hasPrelaunchBypass } from './PrelaunchGate';
import TokenRateTicker from './TokenRateTicker';

export default function SiteHeader({ onScrollTo }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  // Pre-launch: only the front page is accessible, so trim the menu and
  // hide the signup CTA (Sign In stays available). Admins and
  // prelaunch_access accounts (investor evaluation) keep the full menu.
  const prelaunch = isPrelaunch() && !hasPrelaunchBypass(user);
  const navLinks = prelaunch ? NAV_LINKS.filter(l => l.href === '/') : NAV_LINKS;

  const handleLogout = async () => {
    try { await logout(); } catch {}
    setMenuOpen(false);
    navigate('/');
  };

  return (
    <>
      <TokenRateTicker />
      <header
        style={{
          backgroundColor: 'rgba(249,246,240,0.97)',
          borderBottom: `1px solid ${COLORS.border}`,
          padding: '12px 0',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backdropFilter: 'blur(10px)',
        }}
      >
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none', flexShrink: 0 }}>
            <Leaf size={24} color={COLORS.primary} />
            <span style={{ fontSize: 19, fontWeight: 800, color: COLORS.textPrimary, letterSpacing: -0.5, fontFamily: 'Outfit, sans-serif', whiteSpace: 'nowrap' }}>
              ThriveLonger.ai
            </span>
            <img src="/thrivelonger-logo.png" alt="ThriveLonger logo" data-testid="brand-logo-header" style={{ height: 26, width: 'auto', display: 'block' }} />
          </Link>

          {/* Desktop Nav */}
          <nav className="desktop-nav" style={{ display: 'flex', gap: 20 }}>
            {navLinks.map(l => (
              <Link key={l.label} to={l.href} style={{ fontSize: 14, fontWeight: location.pathname === l.href ? 700 : 500, color: location.pathname === l.href ? COLORS.primary : COLORS.textSecondary, textDecoration: 'none', transition: 'color 0.2s', whiteSpace: 'nowrap' }}>
                {l.label}
              </Link>
            ))}
            {onScrollTo && (
              <>
                <button onClick={() => onScrollTo('features')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: COLORS.textSecondary, padding: 0, whiteSpace: 'nowrap' }}>Features</button>
                <button onClick={() => onScrollTo('pricing')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: COLORS.textSecondary, padding: 0, whiteSpace: 'nowrap' }}>Pricing</button>
              </>
            )}
          </nav>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {user ? (
              <>
                <Link to="/dashboard" data-testid="header-dashboard-btn" style={{ padding: '10px 14px', fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }} className="desktop-only">
                  <LayoutDashboard size={15} /> Dashboard
                </Link>
                <button onClick={handleLogout} data-testid="header-logout-btn" style={{ backgroundColor: COLORS.primary, borderRadius: 9999, padding: '12px 18px', fontSize: 13, fontWeight: 700, color: '#FFF', border: 'none', cursor: 'pointer', minHeight: 44, display: 'flex', alignItems: 'center', gap: 6, transition: 'background-color 0.2s' }} className="desktop-only">
                  <LogOut size={15} /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" style={{ padding: '10px 14px', fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, textDecoration: 'none' }} className="desktop-only">
                  Sign In
                </Link>
                {!prelaunch && (
                  <Link to="/register" style={{ backgroundColor: COLORS.primary, borderRadius: 9999, padding: '12px 18px', fontSize: 13, fontWeight: 700, color: '#FFF', textDecoration: 'none', minHeight: 44, display: 'flex', alignItems: 'center', transition: 'background-color 0.2s' }} className="desktop-only">
                    Start Free Trial
                  </Link>
                )}
              </>
            )}
            <button onClick={() => setMenuOpen(true)} className="mobile-only" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <Menu size={26} color={COLORS.textPrimary} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, backgroundColor: 'rgba(0,0,0,0.4)' }} onClick={() => setMenuOpen(false)}>
          <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '85%', maxWidth: 360, backgroundColor: COLORS.background, padding: 24, overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Leaf size={24} color={COLORS.primary} />
                <span style={{ fontSize: 18, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>ThriveLonger.ai</span>
                <img src="/thrivelonger-logo.png" alt="ThriveLonger logo" style={{ height: 26, width: 'auto', display: 'block' }} />
              </div>
              <button onClick={() => setMenuOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={28} color={COLORS.textPrimary} />
              </button>
            </div>

            {navLinks.map(l => (
              <Link key={l.label} to={l.href} onClick={() => setMenuOpen(false)} style={{ display: 'block', padding: '16px 0', borderBottom: `1px solid ${COLORS.border}40`, fontSize: 17, fontWeight: location.pathname === l.href ? 700 : 500, color: location.pathname === l.href ? COLORS.primary : COLORS.textPrimary, textDecoration: 'none' }}>
                {l.label}
              </Link>
            ))}
            {onScrollTo && (
              <>
                <button onClick={() => { setMenuOpen(false); onScrollTo('features'); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '16px 0', borderBottom: `1px solid ${COLORS.border}40`, fontSize: 17, fontWeight: 500, color: COLORS.textPrimary, background: 'none', border: 'none', borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: `${COLORS.border}40`, cursor: 'pointer' }}>Features</button>
                <button onClick={() => { setMenuOpen(false); onScrollTo('pricing'); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '16px 0', borderBottom: `1px solid ${COLORS.border}40`, fontSize: 17, fontWeight: 500, color: COLORS.textPrimary, background: 'none', border: 'none', borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: `${COLORS.border}40`, cursor: 'pointer' }}>Pricing</button>
              </>
            )}

            <div style={{ height: 1, backgroundColor: COLORS.border, margin: '16px 0' }} />
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setMenuOpen(false)} data-testid="mobile-dashboard-btn" style={{ display: 'block', textAlign: 'center', backgroundColor: COLORS.primary, borderRadius: 9999, padding: '16px', fontSize: 16, fontWeight: 700, color: '#FFF', textDecoration: 'none', marginBottom: 12 }}>Open Dashboard</Link>
                <button onClick={handleLogout} data-testid="mobile-logout-btn" style={{ width: '100%', textAlign: 'center', border: `1.5px solid ${COLORS.primary}`, borderRadius: 9999, padding: '14px', fontSize: 16, fontWeight: 600, color: COLORS.primary, backgroundColor: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <LogOut size={16} /> Log out
                </button>
              </>
            ) : (
              <>
                {!prelaunch && (
                  <Link to="/register" onClick={() => setMenuOpen(false)} style={{ display: 'block', textAlign: 'center', backgroundColor: COLORS.primary, borderRadius: 9999, padding: '16px', fontSize: 16, fontWeight: 700, color: '#FFF', textDecoration: 'none', marginBottom: 12 }}>Start Free Trial</Link>
                )}
                <Link to="/login" onClick={() => setMenuOpen(false)} style={{ display: 'block', textAlign: 'center', border: `1.5px solid ${COLORS.primary}`, borderRadius: 9999, padding: '14px', fontSize: 16, fontWeight: 600, color: COLORS.primary, textDecoration: 'none' }}>Sign In</Link>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
